/**
 * OpenMeta — AuthContext créateurs (Phase 8).
 *
 * - Token JWT stocké en localStorage 'om_creator_token'
 * - Injecté automatiquement en Authorization: Bearer via axios instance
 * - État user: null (checking) | object (authenticated) | false (anonymous)
 * - refresh() disponible pour recharger /me après un événement externe
 *   (grant admin, activation post-paiement, etc.)
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'om_creator_token';

const authApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

authApi.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const t = window.localStorage.getItem(STORAGE_KEY);
    if (t) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => ({ ok: false }),
  register: async () => ({ ok: false }),
  logout: () => {},
  refresh: async () => null,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const t = window.localStorage.getItem(STORAGE_KEY);
      if (!t) {
        setUser(false);
        return null;
      }
      const res = await authApi.get('/auth/me');
      setUser(res.data);
      return res.data;
    } catch (err) {
      // token invalide ou expiré → purge
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async ({ email, password }) => {
    try {
      const res = await authApi.post('/auth/login', { email, password });
      const { token, user: u } = res.data;
      window.localStorage.setItem(STORAGE_KEY, token);
      setUser(u);
      return { ok: true, user: u };
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) return { ok: false, code: 'auth_bad_credentials' };
      if (status === 400) return { ok: false, code: 'auth_invalid_input' };
      if (status === 429) return { ok: false, code: 'auth_rate_limit' };
      return { ok: false, code: 'auth_generic' };
    }
  }, []);

  const register = useCallback(async ({ email, password, lang, accepted_terms }) => {
    try {
      const res = await authApi.post('/auth/register', { email, password, lang, accepted_terms });
      const { token, user: u } = res.data;
      window.localStorage.setItem(STORAGE_KEY, token);
      setUser(u);
      return { ok: true, user: u };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 409) return { ok: false, code: 'auth_email_taken' };
      if (status === 400 && /Conditions|Terms/i.test(detail)) return { ok: false, code: 'auth_terms_required', detail };
      if (status === 400) return { ok: false, code: 'auth_invalid_input' };
      if (status === 429) return { ok: false, code: 'auth_rate_limit' };
      return { ok: false, code: 'auth_generic' };
    }
  }, []);

  const logout = useCallback(() => {
    try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: fetchMe }),
    [user, loading, login, register, logout, fetchMe],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export const AUTH_STORAGE_KEY = STORAGE_KEY;
